import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, UserCheck, UserX, Clock, Shield, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserManagement() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [pendingRes, staffRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/users/pending`, { headers }),
        axios.get(`${BACKEND_URL}/api/users/staff`, { headers }),
      ]);

      setPendingUsers(pendingRes.data);
      setStaffUsers(staffRes.data);
    } catch (error) {
      toast.error('Failed to load users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.put(
        `${BACKEND_URL}/api/users/${userId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User approved! They will receive an email notification.');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to approve user');
      console.error(error);
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this registration?')) return;
    
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.put(
        `${BACKEND_URL}/api/users/${userId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User rejected');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to reject user');
      console.error(error);
    }
  };

  const handleDownloadTrainingGuide = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/training-guide/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Vyrus_Training_Guide.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Training guide downloaded');
    } catch (error) {
      toast.error('Failed to download training guide');
      console.error(error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold font-heading tracking-tight" data-testid="user-management-title">
            User Management
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">Manage team access</p>
        </div>
        <Button onClick={handleDownloadTrainingGuide} variant="outline" size="sm">
          <Download className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Training Guide PDF</span>
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-4">
            <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-yellow-400">{pendingUsers.length}</p>
              <p className="text-[10px] sm:text-sm text-muted-foreground truncate">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-4">
            <UserCheck className="h-6 w-6 sm:h-8 sm:w-8 text-green-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-green-400">{staffUsers.length}</p>
              <p className="text-[10px] sm:text-sm text-muted-foreground truncate">Staff</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-primary/10 border-primary/30">
          <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-4">
            <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-primary flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-primary">1</p>
              <p className="text-[10px] sm:text-sm text-muted-foreground truncate">Management</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pending">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="pending" className="relative text-xs sm:text-sm">
            Pending
            {pendingUsers.length > 0 && (
              <span className="ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 bg-yellow-500 text-black text-[10px] sm:text-xs rounded-full">
                {pendingUsers.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="staff" className="text-xs sm:text-sm">Active Staff</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4 sm:mt-6">
          {pendingUsers.length === 0 ? (
            <Card className="p-8 sm:p-12 text-center bg-card border-border/50">
              <Users className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-sm">No pending registrations</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingUsers.map((user) => (
                <Card key={user.id} className="p-3 sm:p-4 bg-card border-border/50">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-base sm:text-lg truncate">{user.name}</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground truncate">{user.email}</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                        Registered: {formatDate(user.created_at)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApprove(user.id)}
                        className="bg-green-500 hover:bg-green-600 flex-1 sm:flex-none"
                        size="sm"
                      >
                        <UserCheck className="h-4 w-4 sm:mr-2" />
                        <span className="sm:inline">Approve</span>
                      </Button>
                      <Button
                        onClick={() => handleReject(user.id)}
                        variant="destructive"
                        className="flex-1 sm:flex-none"
                        size="sm"
                      >
                        <UserX className="h-4 w-4 sm:mr-2" />
                        <span className="sm:inline">Reject</span>
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="staff" className="mt-4 sm:mt-6">
          {staffUsers.length === 0 ? (
            <Card className="p-8 sm:p-12 text-center bg-card border-border/50">
              <Users className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-sm">No staff members yet</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {staffUsers.map((user) => (
                <Card key={user.id} className="p-3 sm:p-4 bg-card border-border/50">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-base sm:text-lg truncate">{user.name}</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground truncate">{user.email}</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                        Joined: {formatDate(user.created_at)}
                      </p>
                    </div>
                    <span className="px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-medium bg-green-500/20 text-green-400 flex-shrink-0">
                      ACTIVE
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
